// sonar.cpd.exclusions

import { getSDKConfig } from '../../shares/lib/getSDKConfig';
import getElementSelectors from '../../shares/lib/getElementSelectors';
import { getSizeOfDevice } from '../../shares/lib/getSizeOfDevice';
import { LogiSDKConfig } from '../../shares/model/config';
import { isTenantFeatureEnabled } from '../../shares/helpers/tenantFeatureHelper';
import { TenantFeature } from '../../shares/enums/tenantFeatures';
import { SwipeCommonMethods, ScreenProps } from './swipeCommon';
import { idleCollapse } from './idleCollapse';

export class SwipeHandler {
  private swipeSDKConfig: LogiSDKConfig;
  private swipe_control: HTMLElement | null;
  private swipeHandle: HTMLElement | null;
  private swipeErrorMsg: HTMLElement | null;
  private swipeSdkContainer: HTMLElement | null;
  private pretX: number;
  private pretY: number;
  private dragStartTop: number;
  private startClickPosY: number;
  private startClickPosX: number;
  private screenProps: ScreenProps;
  private iconPosition: string;
  private iconPositionClass: string;
  private isClickEvent: boolean;
  private currentY: number;
  private isDragging: boolean;
  private activePointerId: number | null;
  private readonly isArrowIconsFeature: boolean;
  private isTransitioning: boolean;

  constructor() {
    const { swipe_control, swipeHandle, swipeErrorMsg, swipeSdkContainer } =
      getElementSelectors();
    this.swipeSDKConfig = getSDKConfig();
    this.swipe_control = swipe_control;
    this.swipeHandle = swipeHandle;
    this.swipeErrorMsg = swipeErrorMsg;
    this.swipeSdkContainer = swipeSdkContainer;
    const iconTopPercent = this.swipeSDKConfig?.swipeGames?.styles.iconTop ?? 0;
    const iconTopPixels = (globalThis.innerHeight * iconTopPercent) / 100;
    this.pretX = 0;
    this.pretY = iconTopPixels;
    this.dragStartTop = 0;
    this.startClickPosY = 0;
    this.startClickPosX = 0;
    this.screenProps = getSizeOfDevice();
    this.iconPosition =
      this.swipeSDKConfig?.swipeGames?.iconPosition || 'right';
    this.iconPositionClass = `game-launcher-at-${this.iconPosition}-position`;
    this.isClickEvent = false;
    this.currentY = 0;
    this.isDragging = false;
    this.activePointerId = null;
    this.isArrowIconsFeature = isTenantFeatureEnabled([
      TenantFeature.ARROW_ICONS,
    ]);
    this.isTransitioning = false;

    if (this.swipe_control) {
      this.swipe_control.style.touchAction = 'none';
    }

    SwipeCommonMethods.applyPositionClasses(this.iconPosition);
    this.setupEventListeners();
    idleCollapse.start();
  }

  private setupEventListeners(): void {
    if (!this.swipe_control) {
      return;
    }

    this.swipe_control.addEventListener('pointerdown', this.handleStart);
    document.addEventListener('pointermove', this.handleMove);

    document.addEventListener('pointerup', this.handleEnd);
    document.addEventListener('pointercancel', this.handleEnd);

    this.swipe_control.addEventListener('mouseenter', () => {
      SwipeCommonMethods.restartArrowAnimation(
        this.swipe_control,
        'SwipeSdk-bounce-arrow 8s ease-in forwards',
      );
    });

    globalThis.addEventListener('resize', this.handleResize);
  }

  private handleStart = (e: PointerEvent): void => {
    if (this.isTransitioning) {
      return;
    }

    if (this.isDragging || (e.pointerType === 'mouse' && e.button !== 0)) {
      return;
    }

    if (this.swipe_control) {
      this.swipe_control.setPointerCapture(e.pointerId);
    }

    e.preventDefault();
    e.stopPropagation();

    if (!this.swipe_control || !this.swipeHandle) {
      return;
    }

    this.isDragging = true;
    this.activePointerId = e.pointerId;
    idleCollapse.expand();
    idleCollapse.pauseCountdown();

    const clientY = e.clientY;
    const clientX = e.clientX;

    const initialSwipePosition_Y =
      (this.screenProps.clientHeight * this.pretY) / 100;
    let swipeTrasform_Y = clientY - initialSwipePosition_Y;
    swipeTrasform_Y = swipeTrasform_Y > 50 ? swipeTrasform_Y : 0;
    this.swipe_control.style.transform = `translate(0px, 0px)`;
    this.dragStartTop =
      Number.parseFloat(globalThis.getComputedStyle(this.swipe_control).top) ||
      0;
    this.startClickPosY = swipeTrasform_Y;
    this.currentY = this.swipeHandle.classList.contains(this.iconPositionClass)
      ? clientY
      : this.startClickPosY + clientY;
    this.startClickPosX = clientX;
    if (this.swipeErrorMsg) {
      this.swipeErrorMsg.style.display = 'none';
    }
    this.isClickEvent = true;
  };

  private handleMove = (e: PointerEvent): void => {
    if (this.isTransitioning) {
      return;
    }

    if (!this.isDragging || e.pointerId !== this.activePointerId) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    if (!this.swipe_control || !this.swipeHandle) {
      return;
    }

    const clientY = e.clientY;
    const clientX = e.clientX;

    if (
      Math.abs(clientY) > globalThis.innerHeight - 50 ||
      Math.abs(clientY) < 50
    ) {
      return;
    }

    const diffY = this.startClickPosY + clientY - this.currentY;
    this.pretY = this.dragStartTop + diffY;

    const diffX = clientX - this.startClickPosX;
    const isAtIconPosition = this.swipeHandle.classList.contains(
      this.iconPositionClass,
    );
    const maxTranslate = this.screenProps.clientWidth;

    const x = isAtIconPosition
      ? this.iconPosition === 'left'
        ? Math.max(Math.min(diffX, 0), -maxTranslate)
        : Math.min(Math.max(diffX, 0), maxTranslate)
      : this.iconPosition === 'left'
        ? Math.min(Math.max(diffX, 0), maxTranslate)
        : Math.max(Math.min(diffX, 0), -maxTranslate);

    this.pretX = x;

    if (this.isArrowIconsFeature) {
      this.calculateSwipeAnimation(x, diffY, maxTranslate);
    } else {
      this.swipe_control.style.transform = `translate(0px, ${diffY}px)`;
      this.swipeHandle.style.transform = `translate(${x}px, 0px)`;
    }

    if (Math.abs(x) > 5 || Math.abs(diffY) > 5) {
      this.isClickEvent = false;
    }
  };

  private handleEnd = (e: PointerEvent): void => {
    if (this.isTransitioning) {
      return;
    }

    if (!this.isDragging || e.pointerId !== this.activePointerId) {
      return;
    }

    if (this.swipe_control) {
      this.swipe_control.releasePointerCapture(e.pointerId);
    }

    e.preventDefault();
    e.stopPropagation();

    this.isDragging = false;
    this.activePointerId = null;
    idleCollapse.resumeCountdown();

    if (!this.swipe_control || !this.swipeHandle) {
      return;
    }

    const allowGameSwipePerc =
      Math.abs(this.pretX / this.screenProps.clientWidth) * 100;

    const shouldToggleState = this.shouldToggleSwipeState(allowGameSwipePerc);

    if (shouldToggleState) {
      this.handleSwipeToggle();
    }
  };

  private shouldToggleSwipeState(swipePercentage: number): boolean {
    if (this.isClickEvent) {
      return true;
    }

    if (swipePercentage >= 40) {
      return true;
    }

    this.resetSwipePosition();
    return false;
  }

  private handleSwipeToggle(): void {
    const isIconPositionClass = this.swipeHandle!.classList.contains(
      this.iconPositionClass,
    );

    if (this.isArrowIconsFeature) {
      this.isTransitioning = true;
      this.handleExpandAnimation(isIconPositionClass);
    } else {
      this.swipeHandle!.classList.toggle(this.iconPositionClass);
      this.resetSwipePosition();
    }

    const swipeSDKFrame = document.querySelector<HTMLElement>(
      '#swipe-sdk-container #swipeSDKFrame',
    );

    if (isIconPositionClass) {
      this.handleSwipeClose(swipeSDKFrame);
    } else {
      this.handleSwipeOpen(swipeSDKFrame);
    }
  }

  private handleSwipeOpen(swipeSDKFrame: HTMLElement | null): void {
    SwipeCommonMethods.handleSwipeOpen(
      this.swipeHandle,
      this.swipeSdkContainer,
      this.swipe_control,
      this.isArrowIconsFeature,
    );
  }

  private handleSwipeClose(swipeSDKFrame: HTMLElement | null): void {
    SwipeCommonMethods.handleSwipeClose(
      this.swipeSdkContainer,
      this.swipe_control,
      this.isArrowIconsFeature,
    );
  }

  private handleResize = (): void => {
    const { swipe_control, swipeHandle } = getElementSelectors();
    this.swipeSDKConfig = getSDKConfig();
    this.swipe_control = swipe_control;
    this.swipeHandle = swipeHandle;
    this.pretX = 0;
    this.pretY = this.swipeSDKConfig?.swipeGames?.styles?.iconTop ?? 0;
    this.startClickPosY = 0;
    this.startClickPosX = 0;
    this.screenProps = getSizeOfDevice();
    this.iconPosition =
      this.swipeSDKConfig?.swipeGames?.iconPosition || 'right';
    this.iconPositionClass = `game-launcher-at-${this.iconPosition}-position`;
    this.isClickEvent = false;
  };

  private resetSwipePosition(): void {
    SwipeCommonMethods.resetSwipePosition(
      this.swipe_control,
      this.swipeHandle,
      this.pretY,
      this.screenProps,
      this.isArrowIconsFeature,
    );
  }

  private calculateSwipeAnimation(
    x: number,
    diffY: number,
    maxTranslate: number,
  ): void {
    SwipeCommonMethods.calculateSwipeAnimation(
      this.swipe_control,
      this.swipeHandle,
      x,
      diffY,
      maxTranslate,
      this.iconPositionClass,
    );
  }

  private handleExpandAnimation(isIconPositionClass: boolean): void {
    SwipeCommonMethods.handleExpandAnimation(
      this.swipe_control,
      this.swipeHandle,
      this.iconPositionClass,
      isIconPositionClass,
      {
        onComplete: () => {
          this.resetSwipePosition();
          this.isTransitioning = false;
        },
        opacityTransition: 'opacity 0.8s ease-out',
        width: '100vw',
        transitionDuration: '0.3s',
      },
    );
  }
}

export const desktopSwipeActions = (): SwipeHandler => {
  return new SwipeHandler();
};
